import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { tokenService, OneTimeCodeForm, PasswordReset, PasswordResetConfirm, useAuthStore } from '@tallo/auth';
import { AppRouteNames, AppRoutes } from '@tallo/investor/navigation';
import { ShowingFilters } from '@tallo/investor/showing';
import { UserRole } from '@tallo/user';
import { TimeFilter } from '@tallo/utility';

import NotFound from '../components/404/not-found.vue';
import TransUnionReport from '../components/application/trans-union-report.vue';
import Auth from '../components/auth/auth.vue';
import EmailConfirmationForm from '../components/auth/email-confirmation-form.vue';
import MissingPasswordSignIn from '../components/auth/missing-password-sign-in.vue';
import SignIn from '../components/auth/sign-in.vue';
import SignUp from '../components/auth/sign-up.vue';
import InvestorAvailabilityPage from '../components/availability/investor-availability-page.vue';
import CompanyOnboardingForm from '../components/company/company-onboarding-form.vue';
import InvestorCalendar from '../components/investor-calendar.vue';
import InvestorShowings from '../components/investor-showings.vue';
import InvestorPortfolio from '../components/portfolio/investor-portfolio.vue';
import { propertyOnboardingRoutes } from '../components/property-onboarding/property-onboarding.routes';
import { ApplicationsFilter, applicationsFilterStages } from '../components/property/consts/applications-filter.const';
import { leadsFilterStages, PropertyLeadsFilter } from '../components/property/consts/leads-filter.const';
import PropertyStatus from '../components/property/pages/dashboard/property-dashboard.vue';
import PropertyDetails from '../components/property/pages/details/property-details.vue';
import PropertyLeads from '../components/property/pages/leads/leads.vue';
import ShowingDetails from '../components/property/pages/showings/showing-details.vue';
import PropertyNavigation from '../components/property/property-navigation.vue';
import Property from '../components/property/property.vue';
import RenterProfileApplicationReview from '../components/renter-profile/renter-application-review.vue';
import RenterProfile from '../components/renter-profile/renter-profile.vue';
import { authorizedGuard } from './authorized.guard';
import { hideOnProdGuard } from './hide-on-prod.guard';
import { oldRoutes } from './routes-old';

const routes: RouteRecordRaw[] = [
  ...oldRoutes,

  {
    path: AppRoutes.HOME,
    name: AppRouteNames.HOME,
    redirect: AppRoutes.INVESTOR_PORTFOLIO,
  },

  {
    name: AppRouteNames.INVESTOR_PORTFOLIO,
    path: AppRoutes.INVESTOR_PORTFOLIO,
    component: InvestorPortfolio,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_AVAILABILITY,
    path: AppRoutes.INVESTOR_AVAILABILITY,
    component: InvestorAvailabilityPage,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_CALENDAR,
    path: AppRoutes.INVESTOR_CALENDAR,
    component: InvestorCalendar,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_SHOWINGS,
    path: AppRoutes.INVESTOR_SHOWINGS,
    component: InvestorShowings,
    meta: { public: false },
    beforeEnter: (to) => {
      if (!to.query.filter) {
        return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
      }

      if (!to.query.timeFilter) {
        return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
      }
    },
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY,
    name: AppRouteNames.INVESTOR_PROPERTY,
    components: {
      default: Property,
      subheader: PropertyNavigation,
    },
    meta: { public: false },
    children: [
      {
        path: AppRoutes.INVESTOR_PROPERTY_DETAILS,
        name: AppRouteNames.INVESTOR_PROPERTY_DETAILS,
        component: PropertyDetails,
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_DASHBOARD,
        name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD,
        component: PropertyStatus,
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_SHOWINGS,
        name: AppRouteNames.INVESTOR_PROPERTY_SHOWINGS,
        component: InvestorShowings,
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ShowingFilters.ALL } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_LEADS,
        name: AppRouteNames.INVESTOR_PROPERTY_LEADS,
        component: PropertyLeads,
        props: {
          title: 'Leads',
          emptyResultTitle: "You don't have any leads yet",
          emptyResultDescription: 'Renters who inquire about properties and their conversations live here',
          filters: Object.values(PropertyLeadsFilter),
          filtersMap: leadsFilterStages,
        },
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: PropertyLeadsFilter.PENDING } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
      {
        path: AppRoutes.INVESTOR_PROPERTY_APPLICATIONS,
        name: AppRouteNames.INVESTOR_PROPERTY_APPLICATIONS,
        component: PropertyLeads,
        props: {
          title: 'Applications',
          emptyResultTitle: 'No applications sent yet',
          emptyResultDescription: 'Renters who made it to the application stage live here',
          filters: Object.values(ApplicationsFilter),
          filtersMap: applicationsFilterStages,
        },
        beforeEnter: (to) => {
          if (!to.query.filter) {
            return { ...to, query: { ...to.query, filter: ApplicationsFilter.PENDING } };
          }

          if (!to.query.timeFilter) {
            return { ...to, query: { ...to.query, timeFilter: TimeFilter.LAST_30_DAYS } };
          }
        },
      },
    ],
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY_RENTER_PROFILE,
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE,
    component: RenterProfile,
    meta: { public: false },
  },
  {
    path: AppRoutes.INVESTOR_PROPERTY_RENTER_PROFILE_APPLICATION_REVIEW,
    name: AppRouteNames.INVESTOR_PROPERTY_RENTER_PROFILE_APPLICATION_REVIEW,
    component: RenterProfileApplicationReview,
    meta: { public: false },
  },
  {
    name: AppRouteNames.INVESTOR_PROPERTY_SHOWING_DETAILS,
    path: AppRoutes.INVESTOR_PROPERTY_SHOWING_DETAILS,
    component: ShowingDetails,
    meta: { public: false },
  },

  {
    ...propertyOnboardingRoutes,
  },

  {
    path: AppRoutes.AUTH,
    component: Auth,
    meta: { public: true },
    beforeEnter: [authorizedGuard],
    children: [
      {
        name: AppRouteNames.SIGN_UP,
        path: AppRoutes.SIGN_UP,
        component: SignUp,
      },
      {
        name: AppRouteNames.SIGN_UP_CONFIRM_EMAIL,
        path: AppRoutes.SIGN_UP_CONFIRM_EMAIL,
        component: EmailConfirmationForm,
      },
      {
        name: AppRouteNames.SIGN_IN,
        path: AppRoutes.SIGN_IN,
        component: SignIn,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.RESET_PASSWORD,
        path: AppRoutes.RESET_PASSWORD,
        component: PasswordReset,
        beforeEnter: [authorizedGuard],
        props: {
          backToSignInRoute: {
            name: AppRouteNames.SIGN_IN,
          },
          forRole: UserRole.INVESTOR,
        },
      },
      {
        name: AppRouteNames.RESET_PASSWORD_CONFIRM,
        path: AppRoutes.RESET_PASSWORD_CONFIRM,
        component: PasswordResetConfirm,
        beforeEnter: [authorizedGuard],
        props: {
          backToSignInRoute: {
            name: AppRouteNames.SIGN_IN,
          },
          afterLoginRoute: {
            name: AppRouteNames.INVESTOR_PORTFOLIO,
          },
          forRole: UserRole.INVESTOR,
        },
      },
      {
        name: AppRouteNames.MISSING_PASSWORD_SIGN_IN,
        path: AppRoutes.MISSING_PASSWORD_SIGN_IN,
        component: MissingPasswordSignIn,
        beforeEnter: [authorizedGuard],
      },
      {
        name: AppRouteNames.ONE_TIME_CODE_SIGN_IN,
        path: AppRoutes.ONE_TIME_CODE_SIGN_IN,
        component: OneTimeCodeForm,
        beforeEnter: [authorizedGuard],
        props: {
          navigationAfterVerification: {
            name: AppRouteNames.INVESTOR_PORTFOLIO,
          },
        },
      },
    ],
  },

  {
    name: AppRouteNames.INVESTOR_COMPANY_ONBOARDING,
    path: AppRoutes.INVESTOR_COMPANY_ONBOARDING,
    meta: { public: false },
    component: CompanyOnboardingForm,
  },

  {
    name: AppRouteNames.APPLICATION_REPORT,
    path: AppRoutes.APPLICATION_REPORT,
    meta: { public: false },
    component: TransUnionReport,
  },

  {
    name: AppRouteNames.DEV_TOOLS,
    path: AppRoutes.DEV_TOOLS,
    meta: { public: false },
    beforeEnter: [hideOnProdGuard],
    component: () => import('../components/dev-tools/dev-tools.vue'),
  },

  {
    name: AppRouteNames.NOT_FOUND,
    path: AppRoutes.NOT_FOUND,
    component: NotFound,
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to: RouteLocationNormalized) => {
  if (!to.meta.public && !tokenService.getToken()) {
    const authStore = useAuthStore();
    authStore.logout();

    return { name: AppRouteNames.SIGN_IN };
  }
});

export default router;
